import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { ACCENTS, GRADIENTS } from '../animations';

export const PageHeader = ({ title, subtitle, actions }) => (!title && !actions) ? null : (
  <Box className="anim-fade-up" sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
    <Box>
      <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 28 } }}>{title}</Typography>
      {subtitle && <Typography color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
    </Box>
    {actions && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>{actions}</Box>}
  </Box>
);

// Round gradient badge that holds an icon.
export const GradientIcon = ({ icon, gradient = GRADIENTS.brand, size = 44 }) => (
  <Box sx={{ width: size, height: size, borderRadius: '30%', background: gradient, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: '0 6px 16px rgb(var(--c-primary-rgb) / 0.30)', '& svg': { fontSize: size * 0.5 } }}>{icon}</Box>
);

export const EmptyState = ({ icon, title, text, action }) => (
  <Paper variant="outlined" className="anim-pop" sx={{ p: { xs: 4, md: 8 }, textAlign: 'center', borderStyle: 'dashed', bgcolor: 'transparent', position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(600px 220px at 50% 0%, rgb(var(--c-primary-rgb) / 0.10), transparent 70%)', pointerEvents: 'none' }} />
    <Box className="float" sx={{ position: 'relative', display: 'inline-grid', placeItems: 'center', mb: 2 }}>
      <Box sx={{ position: 'absolute', width: 84, height: 84, borderRadius: '50%', border: '2px dashed', borderColor: 'primary.light', animation: 'spinSlow 14s linear infinite', opacity: 0.6 }} />
      <GradientIcon icon={icon} size={60} />
    </Box>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    <Typography color="text.secondary" sx={{ maxWidth: 440, mx: 'auto', mb: action ? 2.5 : 0, position: 'relative' }}>{text}</Typography>
    {action}
  </Paper>
);

export const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
export const formatDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// Counts up from 0 to `value` once (and again when the value changes). Falls back to the plain value for non-numbers.
export function AnimatedNumber({ value, decimals = 0, prefix = '', suffix = '', duration = 900 }) {
  const target = typeof value === 'number' ? value : parseFloat(value);
  const numeric = Number.isFinite(target);
  const [shown, setShown] = useState(numeric ? 0 : target);
  const raf = useRef(null);
  useEffect(() => {
    if (!numeric) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setShown(target); return undefined; }
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration), eased = 1 - Math.pow(1 - p, 3);
      setShown(target * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, numeric, duration]);
  if (!numeric) return <>{String(value)}</>;
  return <>{prefix}{shown.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</>;
}

// Stat tile: gradient icon badge, label, big (optionally counting) value.
export const KpiTile = ({ label, value, hint, icon, gradient, decimals = 0, prefix = '', suffix = '', color }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 170, height: '100%', position: 'relative', overflow: 'hidden', display: 'flex', gap: 1.75, alignItems: 'center',
    '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: gradient || GRADIENTS.brand } }}>
    {icon && <GradientIcon icon={icon} gradient={gradient} size={46} />}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, color: color || 'text.primary' }}>
        <AnimatedNumber value={value} decimals={decimals} prefix={prefix} suffix={suffix} />
      </Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  </Paper>
);

// Deterministic colour per customer name -> coloured initial avatar in the tables.
const AVATAR_COLORS = [GRADIENTS.brand, GRADIENTS.sky, GRADIENTS.emerald, GRADIENTS.amber, GRADIENTS.violet];
export function NameAvatar({ name, size = 34 }) {
  const s = String(name || '?');
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return (
    <Box sx={{ width: size, height: size, borderRadius: '30%', background: AVATAR_COLORS[h % AVATAR_COLORS.length], color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: size * 0.42, flexShrink: 0 }}>
      {s.trim()[0]?.toUpperCase() || '?'}
    </Box>
  );
}

export { ACCENTS, GRADIENTS };
